import { isNumber, isString } from '../shared/runtime-values.js';
import { html, render } from 'lit/html.js';
import type { DataLayer } from '../shared/data-layer.js';
import { readMapRoute } from './map-route-data.js';
import { formatDistanceKm, formatDurationSec } from './route-planner-routing.js';
import { t } from './i18n.js';
import './map-route-card.css';

interface RouteCardActions {
  close(): void;
  edit?(): void;
  rename?(name: string): void;
  color?: { value: string; change(color: string): void };
  remove?(): void;
}

/** Planned routes have a task view, including in maps that cannot be edited. */
export function showMapRouteCard(layer: DataLayer, actions: RouteCardActions): boolean {
  const route = readMapRoute(layer);
  if (!route) return false;
  document.querySelector('#data-feature-card')?.remove();
  const card = document.createElement('aside');
  card.id = 'data-feature-card'; card.className = 'map-route-card';
  card.setAttribute('aria-labelledby', 'map-route-card-title');
  const properties = layer.features[0].properties;
  const title = isString(properties.routeTitle) && properties.routeTitle.trim() ? properties.routeTitle : t('map_route.tool');
  const metrics = [
    isNumber(properties.durationSec) && Number.isFinite(properties.durationSec) && properties.durationSec >= 0 ? formatDurationSec(properties.durationSec) : null,
    isNumber(properties.distanceKm) && Number.isFinite(properties.distanceKm) && properties.distanceKm >= 0 ? formatDistanceKm(properties.distanceKm, true) : null,
    t(`ai_map_assistant.${route.travelMode}`),
  ].filter(Boolean).join(' · ');
  let renaming = false;
  const close = () => { card.remove(); actions.close(); };
  const paint = () => render(html`
    <header><h2 id="map-route-card-title">${title}</h2><button type="button" class="route-card-close" aria-label=${t('shell.close')} @click=${close}>×</button></header>
    <p class="route-card-metrics">${metrics}</p>
    <ol class="route-card-stops">${route.routeStops.map((stop, index) => html`
      <li><span class="route-card-number" aria-hidden="true">${index + 1}</span><span class="route-card-address" title=${stop.address}>${stop.address.split(',').slice(0, 2).join(',')}</span></li>
    `)}</ol>
    ${actions.edit ? html`<footer>
      <button type="button" class="route-card-edit" @click=${actions.edit}>${t('route_profile.edit')}</button>
      <details><summary aria-label=${t('data_layers.more_actions').replace('{name}', title)}>⋯</summary><div class="route-card-menu">
        ${actions.rename ? html`<button type="button" @click=${() => { renaming = true; paint(); card.querySelector<HTMLInputElement>('[name="route-name"]')?.select(); }}>${t('data_layers.rename')}</button>` : ''}
        ${actions.color ? html`<label>${t('data_layers.selection_color')}<input type="color" .value=${actions.color.value} @change=${(event: Event) => { if (event.currentTarget instanceof HTMLInputElement) actions.color?.change(event.currentTarget.value); }} /></label>` : ''}
        ${actions.remove ? html`<button type="button" class="route-card-delete" @click=${actions.remove}>${t('shell.delete_selected')}</button>` : ''}
      </div></details>
    </footer>` : ''}
    ${renaming ? html`<form @submit=${(event: SubmitEvent) => {
      event.preventDefault();
      const input = card.querySelector<HTMLInputElement>('[name="route-name"]');
      const name = input?.value.trim();
      if (name) actions.rename?.(name);
    }}><label for="route-card-name">${t('route_profile.name')}</label><input id="route-card-name" name="route-name" .value=${title} maxlength="120" required />
      <div><button type="submit">${t('save.save')}</button><button type="button" @click=${() => { renaming = false; paint(); }}>${t('confirm.cancel')}</button></div>
    </form>` : ''}
  `, card);
  card.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.stopPropagation(); close(); }
  });
  paint(); document.body.appendChild(card);
  return true;
}
